import {
  validateEgressPolicyRuleInput,
  type EgressFallback,
  type EgressPolicyRecord,
  type EgressPolicyRuleInput,
  type EgressPolicyRuleRecord,
  type EgressViaKind,
  type UpdateEgressPolicyRequest,
} from '@nadoutong/contracts';

export const UPSTREAM_ANY = '*';
export const VIA_DIRECT = 'direct';
const REGION_PREFIX = 'region:';
const ID_PREFIX = 'id:';

export interface EgressPolicyDraftRule {
  readonly key: string;
  readonly ingress_region: string;
  readonly upstream_id: string | null;
  readonly upstream_region: string | null;
  readonly via_kind: EgressViaKind;
  readonly via_cluster: string | null;
  readonly fallback: EgressFallback;
  readonly note: string;
}

export interface EgressPolicyRuleRow {
  readonly key: string;
  readonly inherited: boolean;
  readonly draft: EgressPolicyDraftRule;
  readonly issue: string | null;
}

export function pinGlobalFirst(
  policies: readonly EgressPolicyRecord[],
): readonly EgressPolicyRecord[] {
  const global = policies.filter((policy) => policy.is_global_default);
  const rest = policies.filter((policy) => !policy.is_global_default);
  return [...global, ...rest];
}

export function encodeUpstreamSelector(rule: {
  readonly upstream_id: string | null;
  readonly upstream_region: string | null;
}): string {
  if (rule.upstream_id !== null && rule.upstream_id.length > 0) return `${ID_PREFIX}${rule.upstream_id}`;
  if (rule.upstream_region !== null && rule.upstream_region.length > 0) {
    return `${REGION_PREFIX}${rule.upstream_region}`;
  }
  return UPSTREAM_ANY;
}

export function decodeUpstreamSelector(value: string): {
  readonly upstream_id: string | null;
  readonly upstream_region: string | null;
} {
  if (value.startsWith(ID_PREFIX)) {
    return { upstream_id: value.slice(ID_PREFIX.length), upstream_region: null };
  }
  if (value.startsWith(REGION_PREFIX)) {
    return { upstream_id: null, upstream_region: value.slice(REGION_PREFIX.length) };
  }
  return { upstream_id: null, upstream_region: null };
}

export function encodeVia(rule: {
  readonly via_kind: EgressViaKind;
  readonly via_cluster: string | null;
}): string {
  if (rule.via_kind === 'cluster' && rule.via_cluster !== null) return rule.via_cluster;
  return VIA_DIRECT;
}

export function decodeVia(value: string): {
  readonly via_kind: EgressViaKind;
  readonly via_cluster: string | null;
} {
  if (value === VIA_DIRECT || value.length === 0) {
    return { via_kind: 'direct', via_cluster: null };
  }
  return { via_kind: 'cluster', via_cluster: value };
}

export function toDraftRule(rule: EgressPolicyRuleRecord, key = rule.id): EgressPolicyDraftRule {
  return {
    key,
    ingress_region: rule.ingress_region,
    upstream_id: rule.upstream_id,
    upstream_region: rule.upstream_region,
    via_kind: rule.via_kind,
    via_cluster: rule.via_cluster,
    fallback: rule.fallback,
    note: rule.note ?? '',
  };
}

export function emptyDraftRule(key: string): EgressPolicyDraftRule {
  return {
    key,
    ingress_region: '*',
    upstream_id: null,
    upstream_region: null,
    via_kind: 'direct',
    via_cluster: null,
    fallback: 'direct',
    note: '',
  };
}

export function toRuleInput(draft: EgressPolicyDraftRule): EgressPolicyRuleInput {
  return {
    ingress_region: draft.ingress_region,
    upstream_id: draft.upstream_id,
    upstream_region: draft.upstream_region,
    via_kind: draft.via_kind,
    via_cluster: draft.via_kind === 'cluster' ? draft.via_cluster : null,
    fallback: draft.via_kind === 'direct' ? 'direct' : draft.fallback,
    note: draft.note.trim().length === 0 ? null : draft.note.trim(),
  };
}

export function validateDraftRule(draft: EgressPolicyDraftRule): string | null {
  return validateEgressPolicyRuleInput(toRuleInput(draft));
}

export function buildDisplayedRows(
  own: readonly EgressPolicyDraftRule[],
  inherited: readonly EgressPolicyRuleRecord[],
): readonly EgressPolicyRuleRow[] {
  const ownRows = own.map((draft) => ({
    key: draft.key,
    inherited: false,
    draft,
    issue: validateDraftRule(draft),
  }));
  const inheritedRows = inherited.map((rule) => {
    const draft = toDraftRule(rule, `inherited:${rule.id}`);
    return {
      key: draft.key,
      inherited: true,
      draft,
      issue: null,
    };
  });
  return [...ownRows, ...inheritedRows];
}

export function inheritedRulesFor(
  selected: EgressPolicyRecord,
  global: EgressPolicyRecord | null,
): readonly EgressPolicyRuleRecord[] {
  if (selected.is_global_default || global === null || global.id === selected.id) return [];
  return [...global.rules].sort((left, right) => left.position - right.position);
}

export function buildUpdatePayload(
  policy: EgressPolicyRecord,
  drafts: readonly EgressPolicyDraftRule[],
  emergencyDirect: boolean,
): UpdateEgressPolicyRequest {
  return {
    name: policy.name,
    description: policy.description,
    emergency_direct: emergencyDirect,
    expected_version: policy.version,
    rules: drafts.map(toRuleInput),
  };
}

export function firstValidationIssue(drafts: readonly EgressPolicyDraftRule[]): string | null {
  for (const draft of drafts) {
    const issue = validateDraftRule(draft);
    if (issue !== null) return issue;
  }
  if (drafts.length === 0) return 'rules must be a non-empty array';
  return null;
}
